import { genId } from '../lib/util';
import { KYC_ITEMS } from './constants';
import type { CaseTrust } from './initial-cases';

export const createEmptyKyc = (): Record<string, { verified: boolean; evidence: unknown[] }> => {
  const kyc: Record<string, { verified: boolean; evidence: unknown[] }> = {};
  Object.keys(KYC_ITEMS).forEach((key) => {
    kyc[key] = { verified: false, evidence: [] };
  });
  return kyc;
};

// #545 — THE SHAPE `cases.trust` WAS ALWAYS SUPPOSED TO HAVE.
//
// There has never been a factory for it, which is the whole defect: `createCase`
// (state/createCase.ts) sends `kyc` and `onboarding` and no `trust`, so the
// column takes its `'{}'::jsonb` default and the staff tab rendered its nine
// controls against `undefined`. Seeded cases hid it — they carry a populated
// `trust` from `trustFor` in the API's seed script — so only a case created in
// the SPA was affected, and `trust: any` meant the compiler had nothing to say.
//
// The names are the STORED ones and not the intake payload's; `TRUST_FIELD_MAP`
// in apps/api/src/routes/intake.ts is the mapping, and the note there says why.
// `mapIntakeToTrust` builds the same shape server-side for a promoted intake
// case, so this file and that function have to agree — TrustTab.test.tsx pins
// this one against the controls the tab actually renders, the same arrangement
// `createEmptyOnboarding()` got in #544 after a mutation survived without it.
export const createEmptyTrust = (): CaseTrust => ({
  // `null`, not `''`: the tab's select has an explicit "— Select —" option with
  // an empty value, and "not yet assessed" is the honest reading of a new case.
  tcpa: null,
  autodialer: false,
  consent: '',
  optOut: '',
  dncScrub: false,
  reassignedDb: false,
  complaintEmail: '',
  complaintPhone: '',
  privacyUrl: '',
  // No `customerSupplied`. A case that did not come through intake has no
  // applicant answers to record, and an empty object here would claim otherwise.
});

/**
 * A `cases.trust` value that is safe to render (#545).
 *
 * Fills in whatever the stored object is missing — `{}` included, which is what
 * every case created through `createCase` carries — so the tab's inputs are
 * CONTROLLED. Call it once at the top of the tab and read the result; do not
 * spread `c.trust` directly into a `value` or `checked` prop. Both of those
 * props are optional in React's own types, so `undefined` is assignable and the
 * compiler will not stop you; the test is what stops you.
 *
 * The stored value wins over the default for every key it has, `customerSupplied`
 * included, so normalising never overwrites an analyst's answer or an
 * applicant's recorded one.
 */
export const normalizeTrust = (raw: Partial<CaseTrust> | null | undefined): CaseTrust => ({
  ...createEmptyTrust(),
  ...(raw ?? {}),
});

export const createEmptyOnboarding = () => ({
  status: 'DRAFT',
  submittedAt: null as string | null,
  submittedBy: null as string | null,
  oaReviewDeadline: null as string | null,
  oaReviewCompletedAt: null as string | null,
  oaForwardDeadline: null as string | null,
  oaForwardedAt: null as string | null,
  agreementExecuted: false,
  agreementDate: null as string | null,
  agreementRef: '',
  lastRecertDate: null as string | null,
  recertDueDate: null as string | null,
  recertStatus: null as string | null,
  recertNotes: '',
  data: {
    legalName: '', fein: '', dba: '', website: '', orgType: '', address: '', city: '', state: '', zip: '', jurisdiction: '',
    dunsBradstreet: '', corpRegNumber: '',
    billingContact: '', billingEmail: '', billingPhone: '', billingAddress: '',
    // #685 — the four billing-address parts an intake-sourced case carries.
    //
    // Seeded EMPTY here, and that is what a hand-typed case keeps: the
    // Onboarding tab renders the four boxes only once one of them holds
    // something, so a case an analyst types in at the desk gets the single
    // `billingAddress` box it has always had. This factory is the authority for
    // the names both intake mappers project onto (see the note on
    // `mapIntakeToOnboardingData`), which is why they are declared here rather
    // than appearing for the first time on a promoted case.
    billingStreet: '', billingCity: '', billingState: '', billingZip: '',
    authorizerName: '', authorizerEmail: '', authorizerTitle: '',
    // #567 — WHICH KIND of authorizer, and the evidence when it is an agent
    // (BCBP fn. 3). `'employee'` and not `''`, which is the one place this
    // factory differs from the intake contract on purpose: intake REFUSES an
    // unanswered relationship because the applicant is being asked, while a
    // case that starts life at the staff desk has nobody to ask yet and has
    // to render something. `'employee'` is the reading that gates nothing,
    // so an analyst who types a case in by hand meets no evidence bar they
    // did not opt into — and the moment they set it to `authorized_agent`
    // the gate applies.
    authorizerRelationship: 'employee',
    agentAuthorizationTitle: '', agentAuthorizationDate: '', agentAuthorizationSignatory: '',
    // #721 — the REGISTRANT's statement that they have already sent the Letter
    // of Authorization to 382, which an intake case carries from the
    // application. `false` here because a staff-desk case was typed in by an
    // analyst and no registrant said anything: an analyst opening one must not
    // read a claim nobody made. It does not gate anything — the gate is
    // `agentAuthorizationVerified` below, which is 382's own finding.
    agentAuthorizationSubmitted: false,
    // The analyst's attestation that they saw the authorization document,
    // with who and when. This is the verification method the conformance
    // record now names, so the actor and the time are part of it rather than
    // decoration: an unattributed tick records that somebody, at some point,
    // said yes.
    agentAuthorizationVerified: false,
    agentAuthorizationVerifiedBy: '', agentAuthorizationVerifiedAt: '',
    sicCode: '', sicDescription: '', profLicense: '',
    displayName: '', callReason: '', callLogoUrl: '',
    tns: '',
    useAutodialer: false, consentProcess: '', optOutProcess: '', privacyUrl: '',
    complaintEmail: '', complaintPhone: '',
    certNoShaft: false,
    certIpOwnership: false,
    certDmcaProcess: false,
    // #544 — the two BCBP representations and warranties about authority.
    //
    // The submit gate reads `d[field] || false`, so it would work without these
    // keys — but this factory is what `apps/api/src/routes/intake.ts` names as
    // the authority for `onboarding.data`'s field names, and leaving them out
    // would put two attestation keys in the gate and in the mapper and nowhere
    // in the shape. That is the drift class #518 and #519 were both about.
    //
    // `false`, like the other three: a case that starts life outside intake has
    // made no attestations, and an unticked box is the honest rendering of that.
    certTnRights: false,
    certAuthorityToSubmit: false,
    notes: '',
  },
});

// #623 — `createShaftDispute` and `createIpDispute` USED TO LIVE HERE and are
// gone on purpose.
//
// They were browser-side row builders, unreferenced by anything, that computed
// `oaNotifyDeadline` (10 business days) and `notifyDeadline` / `suspendDeadline`
// (1 calendar day) off the analyst's local clock. Disputes are created through
// POST /api/v1/cases/:id/disputes/{shaft,ip}, which returns the canonical row,
// so nothing in the SPA ever called them — which is exactly how both notify
// deadline columns stayed NULL in production while this file appeared to be
// computing them. Deleting the dead copy is the point: the arithmetic now lives
// once, on the server, next to the insert that persists it. Leaving a second
// unreachable copy here is the drift class #545 and #518 were about.
//
// The row SHAPE the SPA reads is `ShaftDispute` / `IpDispute` in
// apps/spa/src/api/disputes.ts, which mirrors the server's table.
